import React, { useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './FeedbackBox.css';

interface FeedbackBoxProps {
  memberEmail: string;
}

type FeedbackType = 'suggestion' | 'bug' | 'compliment' | 'question' | 'other';

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'compliment', label: 'Compliment' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

const FeedbackBox: React.FC<FeedbackBoxProps> = ({ memberEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('suggestion');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(memberEmail);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MAX_CHARS = 2000;

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.length > MAX_CHARS) {
      setError(`Message is too long (max ${MAX_CHARS} characters)`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await bridgeClubAPI.submitFeedback({
        feedback_type: feedbackType,
        message: message.trim(),
        reply_email: email || memberEmail,
      });

      setSuccess(true);
      setMessage('');
      setFeedbackType('suggestion');
    } catch (err: any) {
      setError(err.error || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSuccess(false);
    setError(null);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setEmail(memberEmail);
    setSuccess(false);
    setError(null);
  };

  if (!isOpen) {
    return (
      <button className="feedback-toggle" onClick={handleOpen}>
        <span className="feedback-toggle-icon">💬</span>
        <span className="feedback-toggle-text">Leave a Comment or Suggestion</span>
      </button>
    );
  }

  if (success) {
    return (
      <div className="feedback-box feedback-success-box">
        <div className="feedback-success">
          <span className="success-icon">✓</span>
          <h4>Thank you!</h4>
          <p>Check your email for confirmation.</p>
        </div>
        <button className="feedback-close-btn" onClick={handleClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="feedback-box">
      <div className="feedback-header">
        <h4>Share Your Feedback</h4>
        <button className="feedback-close-x" onClick={handleClose}>×</button>
      </div>

      <div className="feedback-form">
        <div className="feedback-field">
          <label>Type</label>
          <select
            className="feedback-type"
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
          >
            {FEEDBACK_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="feedback-field">
          <label>Your Email</label>
          <input
            type="email"
            className="feedback-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
          <span className="feedback-hint">We'll send you a confirmation</span>
        </div>

        <div className="feedback-field">
          <label>Message</label>
          <textarea
            className="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind?"
            rows={4}
            maxLength={MAX_CHARS}
          />
          <div className="feedback-char-count">
            <span className={message.length > MAX_CHARS * 0.9 ? 'warning' : ''}>
              {message.length}/{MAX_CHARS}
            </span>
          </div>
        </div>

        {error && <div className="feedback-error">{error}</div>}

        <div className="feedback-footer">
          <button
            className="feedback-cancel-btn"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="feedback-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
          >
            {submitting ? 'Sending...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackBox;
